import clsx from 'clsx';
import {ChangeEvent, FC, InputHTMLAttributes, RefObject, SVGProps, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import CrossIcon from '@/icons/CrossIcon';
import PasswordHiddenIcon from '@/icons/PasswordHiddenIcon';
import PasswordVisibleIcon from '@/icons/PasswordVisibleIcon';
import Label, {ILabel} from '@/ui/Label';
import ValidationMessage from '@/ui/ValidationMessage';
import styles from './styles.module.scss';

interface IInputProps {
    Icon?: FC<SVGProps<SVGSVGElement>> | null;
    SecondIcon?: FC<SVGProps<SVGSVGElement>> | null;
    label?: ILabel;
    error?: string | null;
    isHookForm?: boolean;
    allowClear?: boolean;
    withRequiredMark?: boolean;
    onClearCallback?: () => void;
    inputRef?: RefObject<HTMLInputElement> | null;
    inputProps?: InputHTMLAttributes<HTMLInputElement>;
    fieldStyles?: string[];
    inputStyles?: string[];
    labelStyles?: string[];
}

const Input = ({
    Icon = null,
    SecondIcon = null,
    label = null,
    error = null,
    isHookForm = true,
    allowClear = true,
    withRequiredMark = false,
    onClearCallback = () => {},
    inputRef = null,
    inputProps = {},
    fieldStyles = [],
    inputStyles = [],
    labelStyles = []
}: IInputProps) => {
    const {setValue, watch} = useFormContext() || {};
    const {name, type, ...inputPropsRest} = inputProps;
    const isPassword = type === 'password';
    const [inputType, setInputType] = useState(type);
    const passwordType = inputType === 'password';

    const notEmpty = isHookForm ? !!watch && !!watch(name || '') : !!inputProps?.value;

    const onClear = () => {
        if (!name) return;
        if (isHookForm && !!setValue) {
            setValue(name, '', {shouldValidate: true, shouldDirty: true});
        } else {
            const onChange = inputProps?.onChange;
            !!onChange && onChange({target: {value: ''}} as ChangeEvent<HTMLInputElement>);
        }
        onClearCallback();
    };

    return (
        <div className={clsx(styles.field, ...fieldStyles)}>
            <Label
                label={label}
                htmlFor={name || inputProps?.id}
                inputFilled={notEmpty}
                withRequiredMark={withRequiredMark}
                labelStyles={labelStyles}
            />
            <label className={styles.wrapper}>
                {!!Icon && (
                    <div className={styles.icon}>
                        <Icon />
                    </div>
                )}
                <input
                    id={name}
                    name={name}
                    type={inputType}
                    ref={inputRef}
                    {...inputPropsRest}
                    className={clsx(
                        styles.input,
                        !!Icon && styles.withIcon,
                        !!error && styles.withError,
                        (!!SecondIcon || allowClear || isPassword) && styles.withSecondIcon,
                        ...inputStyles
                    )}
                />
                {!!SecondIcon && !isPassword && (
                    <div className={clsx(styles.icon, styles.second)}>
                        <SecondIcon />
                    </div>
                )}
                {allowClear && !isPassword && (
                    <button
                        type={'button'}
                        aria-label={'Clear input'}
                        title={'Clear input'}
                        onClick={onClear}
                        className={clsx(styles.icon, styles.clearBtn, notEmpty && styles.showClear)}
                    >
                        <CrossIcon />
                    </button>
                )}
                {isPassword && (
                    <button
                        type={'button'}
                        aria-label={passwordType ? 'Show password' : 'Hide password'}
                        title={passwordType ? 'Show password' : 'Hide password'}
                        onClick={() => setInputType(prev => (prev === 'password' ? 'text' : 'password'))}
                        className={clsx(styles.icon, styles.passwordBtn)}
                    >
                        {passwordType ? <PasswordHiddenIcon /> : <PasswordVisibleIcon />}
                    </button>
                )}
            </label>
            <ValidationMessage error={error} />
        </div>
    );
};

export default Input;
